"use client";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError } from "@/lib/api";
import type { AwardResult } from "@/lib/api/awards";
import { getOperationByKey, type CommandOptions } from "@/lib/api/suppliers";
import { createCommandKey } from "@/lib/commands/command-key";
import {
  awardCompletion,
  awardScope,
  type AwardIntent,
  type AwardOperation,
} from "@/lib/commands/award-operation-store";
import {
  awardFeedback,
  type AwardFeedback,
} from "@/lib/commands/award-feedback";
import { eventAuthorityLost } from "@/lib/commands/event-feedback";
import { eventBindingKey } from "@/lib/commands/event-operation-store";
import { invalidateEvent } from "@/lib/commands/event-invalidation";
import type { EventRuntimeConfig } from "@/components/events/event-runtime";
import { useAwardRecovery } from "./use-award-recovery";
type Sender = (options: CommandOptions) => Promise<{ data: AwardResult }>;
interface Runtime {
  config: EventRuntimeConfig;
  records: AwardOperation[];
  ready: boolean;
  available: boolean;
  pending: boolean;
  epoch: number;
  generation: number;
  terminal?: "Completed" | "Failed";
  lookupFailed: boolean;
  denied: (scope: string) => boolean;
  deny: (scope: string, error: unknown) => void;
  retry: (scope: string) => void;
  blocked: (scope: string) => boolean;
  completion: (scope: string) => number;
  restore: () => void;
  dismiss: () => void;
  run: (
    intent: AwardIntent,
    send: Sender,
  ) => Promise<{ result?: AwardResult; feedback?: AwardFeedback }>;
  lookup: (row: AwardOperation) => Promise<void>;
}
const Context = createContext<Runtime | null>(null);
export function AwardRuntimeProvider({
  config,
  children,
}: {
  config: EventRuntimeConfig;
  children: ReactNode;
}) {
  const recovery = useAwardRecovery(config.binding),
    latest = useRef(config),
    active = useRef<AbortController | null>(null),
    mounted = useRef(false),
    denied = useRef(new Set<string>());
  const [epoch, setEpoch] = useState(0),
    [generation, setGeneration] = useState(0),
    [pending, setPending] = useState(false),
    [lookupFailed, setLookupFailed] = useState(false),
    [terminal, setTerminal] = useState<Runtime["terminal"]>(),
    [completed, setCompleted] = useState<Record<string, number>>({});
  useEffect(() => {
    latest.current = config;
  }, [config]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      active.current?.abort();
    };
  }, []);
  const alive = (scope: string, signal: AbortSignal) =>
    mounted.current && !signal.aborted && !denied.current.has(scope);
  const blocked = (scope: string) =>
    !recovery.available ||
    pending ||
    denied.current.has(scope) ||
    recovery.records.some((x) => awardScope(x) === scope);
  function deny(scope: string, error: unknown) {
    active.current?.abort();
    denied.current.add(scope);
    setEpoch((x) => x + 1);
    try {
      recovery
        .read()
        .filter((x) => awardScope(x) === scope)
        .forEach((x) => recovery.remove(x.idempotencyKey));
    } catch {}
    if (error instanceof ApiError && error.kind === "unauthorized")
      latest.current.invalidate(error);
  }
  function finish(row: AwardOperation, recovered: boolean) {
    if (!recovery.remove(row.idempotencyKey)) return false;
    if (recovered) {
      setTerminal("Completed");
      setCompleted((old) => ({
        ...old,
        [awardScope(row)]: (old[awardScope(row)] ?? 0) + 1,
      }));
    }
    setGeneration((x) => x + 1);
    if (row.eventId) invalidateEvent(row.eventId);
    return true;
  }
  async function perform(
    row: AwardOperation,
    signal: AbortSignal,
    send: Sender,
  ) {
    const scope = awardScope(row);
    try {
      if (recovery.read().some((x) => awardScope(x) === scope))
        return { feedback: "blocked" as const };
      if (!(await latest.current.verify(signal)))
        return { feedback: "forbidden" as const };
      if (!alive(scope, signal)) return { feedback: "unknown" as const };
      if (!recovery.save(row)) return { feedback: "storage" as const };
      const response = await send({
        tenantId: config.binding.tenantId,
        csrfToken: latest.current.csrfToken,
        idempotencyKey: row.idempotencyKey,
        signal,
      });
      if (!alive(scope, signal) || !awardCompletion(response.data, row))
        return { feedback: "unknown" as const };
      return finish(row, false)
        ? { result: response.data }
        : { feedback: "storage" as const };
    } catch (error) {
      if (!alive(scope, signal)) return { feedback: "unknown" as const };
      const feedback = awardFeedback(error);
      if (feedback !== "unknown") recovery.remove(row.idempotencyKey);
      if (eventAuthorityLost(error)) deny(scope, error);
      else setGeneration((x) => x + 1);
      return { feedback };
    }
  }
  async function run(intent: AwardIntent, send: Sender) {
    if (active.current || blocked(awardScope(intent)))
      return { feedback: "blocked" as const };
    if (!navigator.locks) return { feedback: "storage" as const };
    const abort = new AbortController();
    active.current = abort;
    setPending(true);
    const row = {
      ...intent,
      idempotencyKey: createCommandKey("award"),
      startedAt: Date.now(),
    };
    try {
      return await navigator.locks.request(
        eventBindingKey(config.binding),
        { ifAvailable: true },
        (lock) =>
          lock
            ? perform(row, abort.signal, send)
            : Promise.resolve({ feedback: "blocked" as const }),
      );
    } catch {
      return { feedback: "storage" as const };
    } finally {
      if (active.current === abort) active.current = null;
      if (mounted.current) setPending(false);
    }
  }
  async function lookup(row: AwardOperation) {
    const scope = awardScope(row);
    if (active.current || denied.current.has(scope)) return;
    const abort = new AbortController();
    active.current = abort;
    setPending(true);
    setLookupFailed(false);
    try {
      if (!(await latest.current.verify(abort.signal))) return;
      const { data } = await getOperationByKey(
        { tenantId: config.binding.tenantId, signal: abort.signal },
        row.operationType,
        row.idempotencyKey,
      );
      if (!alive(scope, abort.signal)) return;
      if (data.state === "Failed") {
        if (recovery.remove(row.idempotencyKey)) setTerminal("Failed");
        return;
      }
      if (data.state !== "Completed" || !awardCompletion(data.result, row)) {
        setLookupFailed(true);
        return;
      }
      finish(row, true);
    } catch (error) {
      if (alive(scope, abort.signal)) {
        if (eventAuthorityLost(error)) deny(scope, error);
        else setLookupFailed(true);
      }
    } finally {
      if (active.current === abort) active.current = null;
      if (mounted.current) setPending(false);
    }
  }
  return (
    <Context.Provider
      value={{
        config,
        records: recovery.records,
        ready: recovery.ready,
        available: recovery.available,
        pending,
        epoch,
        generation,
        terminal,
        lookupFailed,
        denied: (id) => denied.current.has(id),
        deny,
        retry: (id) => {
          denied.current.delete(id);
          setEpoch((x) => x + 1);
        },
        blocked,
        completion: (id) => completed[id] ?? 0,
        restore: recovery.sync,
        dismiss: () => setTerminal(undefined),
        run,
        lookup,
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function useAwardRuntime() {
  const value = useContext(Context);
  if (!value) throw new Error("Award runtime is unavailable");
  return value;
}
